import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { RichText } from '@/components/RichText';

/** Collapsed height in px. Roughly the lead paragraph plus the first few
 *  bullets — enough to decide whether to read on, which is the only job the
 *  collapsed state has. */
const COLLAPSED_MAX_PX = 260;

/** Below this much overflow, collapsing costs a click and saves almost
 *  nothing, so the control is not worth showing. */
const MIN_OVERFLOW_PX = 80;

/**
 * Long authored prose, clamped until asked for.
 *
 * The week narratives run to well over a thousand words. Rendered in full they
 * are a wall of grey a student scrolls past, and the topic's practice and
 * resources — the things they came for — end up below the fold. Clamped, the
 * lead and the first bullets are what they land on and the rest is one click
 * away.
 *
 * Measured rather than assumed: a short description must not grow a pointless
 * "Read more", so the control appears only once the content genuinely overflows.
 */
export function ExpandableProse({
  html,
  className,
  labelMore = 'Read the full overview',
  labelLess = 'Show less',
}: {
  html: string;
  className?: string;
  labelMore?: string;
  labelLess?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Re-measure on resize: the same prose overflows at 390px and does not at
  // 1400px, and a control that is right only at first paint is a bug on every
  // rotate and every window drag.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflows(el.scrollHeight > COLLAPSED_MAX_PX + MIN_OVERFLOW_PX);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [html]);

  const clamped = overflows && !expanded;

  return (
    <div>
      <div className="relative">
        <div
          ref={ref}
          className={className}
          style={clamped ? { maxHeight: COLLAPSED_MAX_PX, overflow: 'hidden' } : undefined}
        >
          <RichText html={html} />
        </div>
        {/* Fades the clipped line rather than guillotining it, so it reads as
            "there is more" instead of "this text is broken". */}
        {clamped && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent"
          />
        )}
      </div>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-2 inline-flex items-center gap-1 rounded-lg text-[13px] font-semibold text-[#ff5a1f] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff5a1f]"
        >
          {expanded ? labelLess : labelMore}
          <ChevronDown className={`size-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
}
